/**
 * 매뉴얼 라이브 예제 — ` ```composer-example ` 코드펜스를 **실제 Composer 렌더러**로 그린다.
 *
 * 왜 라이브인가: Composer는 시각 도구라 스크린샷이 즉시 낡는다. rc(`@safetyhub/report-composer`)가
 * 독립 패키지라 문서 안에서 같은 엔진을 돌릴 수 있고, 그러면 예제는 엔진이 바뀌어도 따라 최신이 된다.
 *
 * 렌더 진입점은 `renderNode` — `RenderTemplate`은 A4 페이지 셸(폭 794px·여백·흰 배지)까지 그리므로
 * 문서 본문에 끼워 넣기엔 과하다. 예제는 **본문 노드 조각**만 필요해 가장 얇은 경로를 쓴다.
 * (RenderTemplate이 페이지 컨테이너에 주는 기본 폰트는 여기서 컨테이너에 직접 명시해 맞춘다.)
 */
import { Component, useMemo, type ReactNode } from 'react';
import { renderNode, type ComposerNode, type RenderData } from '@safetyhub/report-composer';

/** 예제 펜스의 JSON 스키마 — 최소 형태는 `{ "body": [...] }`. */
export interface ComposerExampleSpec {
  /** 예제 캡션. 없으면 캡션 없이 그림만 */
  title?: string;
  /** 그릴 본문 노드 — `ReportTemplate.body`와 같은 배열 */
  body: ComposerNode[];
  /** resolve된 바인딩 값 맵(bindingKey → 값). 없으면 빈 맵 */
  data?: RenderData;
  /** 예제 렌더 폭(px). 실제 A4 본문 폭보다 좁게 잡아 문서 안에서 읽히게 한다 */
  width?: number;
}

export type ComposerExampleParse =
  | { ok: true; spec: ComposerExampleSpec }
  | { ok: false; message: string };

/** 예제 기본 폭 — A4 본문(794px - 여백)보다 좁게. `width`로 펜스에서 덮을 수 있다. */
const DEFAULT_EXAMPLE_WIDTH = 620;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * 펜스 원문(JSON) → 예제 스펙.
 *
 * **던지지 않는다.** 문서 한 곳의 오타가 문서 전체를 깨뜨리면 안 되므로 실패는 값으로 돌려주고
 * 호출부가 조용한 안내로 대체한다.
 */
export function parseComposerExample(source: string): ComposerExampleParse {
  let parsed: unknown;
  try {
    parsed = JSON.parse(source);
  } catch (error) {
    return { ok: false, message: error instanceof Error ? error.message : 'JSON을 읽지 못했습니다.' };
  }

  if (!isPlainObject(parsed)) {
    return { ok: false, message: '예제는 JSON 객체여야 합니다.' };
  }
  if (!Array.isArray(parsed.body)) {
    return { ok: false, message: 'body(노드 배열)가 필요합니다.' };
  }
  // 노드마다 kind가 있어야 렌더러가 분기할 수 있다 — 여기서 걸러야 렌더 도중 터지지 않는다.
  const badIndex = parsed.body.findIndex(
    (node) => !isPlainObject(node) || typeof node.kind !== 'string',
  );
  if (badIndex >= 0) {
    return { ok: false, message: `body[${badIndex}]에 kind가 없습니다.` };
  }
  if (parsed.data !== undefined && !isPlainObject(parsed.data)) {
    return { ok: false, message: 'data는 JSON 객체여야 합니다.' };
  }
  if (parsed.width !== undefined && (typeof parsed.width !== 'number' || !(parsed.width > 0))) {
    return { ok: false, message: 'width는 양수여야 합니다.' };
  }

  return {
    ok: true,
    spec: {
      title: typeof parsed.title === 'string' ? parsed.title : undefined,
      body: parsed.body as ComposerNode[],
      data: (parsed.data as RenderData | undefined) ?? {},
      width: parsed.width as number | undefined,
    },
  };
}

/** 실패 안내 — 문서를 멈추지 않고, 원문은 접어서 디버깅용으로만 남긴다. */
function ExampleFallback({ source, reason }: { source: string; reason?: string }) {
  return (
    <div className="my-4 rounded-card border border-dashed border-gray-300 bg-gray-50 px-4 py-4 text-sm text-gray-500">
      <p className="m-0">예제를 그리지 못했습니다.</p>
      <details className="mt-2">
        <summary className="cursor-pointer text-xs text-gray-400">예제 원문 보기</summary>
        {reason && <p className="mt-1 mb-0 text-xs text-gray-400">{reason}</p>}
        <pre className="mt-1 max-h-64 overflow-auto rounded bg-gray-900 p-3 font-mono text-xs text-gray-100">
          {source}
        </pre>
      </details>
    </div>
  );
}

/**
 * 렌더 예외 경계 — 파싱을 통과해도 노드 조합에 따라 엔진이 던질 수 있다.
 * 예제 하나의 실패가 문서 전체를 흰 화면으로 만들지 않게 여기서 끊는다.
 */
class ExampleBoundary extends Component<
  { source: string; children: ReactNode },
  { failed: boolean; reason?: string }
> {
  state = { failed: false, reason: undefined as string | undefined };

  static getDerivedStateFromError(error: unknown) {
    return { failed: true, reason: error instanceof Error ? error.message : String(error) };
  }

  render() {
    if (this.state.failed) {
      return <ExampleFallback source={this.props.source} reason={this.state.reason} />;
    }
    return this.props.children;
  }
}

/**
 * 예제 렌더 컨테이너.
 *
 * - **읽기 전용**: 문서 안이라 저장할 곳이 없다. 포인터 이벤트를 막아 클릭·드래그·편집이 안 되게 한다.
 * - **본문을 밀지 않는다**: 예제 폭이 문서보다 넓어도 컨테이너 안에서만 가로 스크롤한다.
 * - **임계 스타일은 인라인**: rc는 앱 CSS에 없는 Tailwind 유틸을 쓸 수 있어(클래스는 붙는데 스타일이
 *   안 먹는 알려진 함정) 테두리·배경·여백·폰트는 클래스에 기대지 않고 style로 명시한다.
 */
function ExampleCanvas({ spec }: { spec: ComposerExampleSpec }) {
  const data = spec.data ?? {};
  return (
    <div
      style={{
        overflowX: 'auto',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        backgroundColor: '#f9fafb',
        padding: '16px',
      }}
    >
      <div
        // 포인터/선택 차단 — 예제는 보여주기 전용이다.
        style={{
          width: `${spec.width ?? DEFAULT_EXAMPLE_WIDTH}px`,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#ffffff',
          // RenderTemplate 페이지 컨테이너와 같은 기본 폰트(노드가 명시하면 그 값이 이긴다).
          fontFamily: "'Noto Serif KR', serif",
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        {spec.body.map((node) => renderNode(node, data))}
      </div>
    </div>
  );
}

/** 문서에 박히는 라이브 예제 한 덩어리 — 펜스 원문을 받아 파싱→렌더까지. */
export function ComposerExample({ source }: { source: string }) {
  const parsed = useMemo(() => parseComposerExample(source), [source]);

  if (!parsed.ok) {
    return <ExampleFallback source={source} reason={parsed.message} />;
  }

  return (
    <figure className="my-4">
      <ExampleBoundary source={source}>
        <ExampleCanvas spec={parsed.spec} />
      </ExampleBoundary>
      {parsed.spec.title && (
        <figcaption className="mt-2 text-center text-xs text-gray-500">
          {parsed.spec.title}
        </figcaption>
      )}
    </figure>
  );
}

export default ComposerExample;
